import { URL_CREATE_NEW_JOB } from '@seek/adv-constants';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Text,
  Stack,
  Columns,
  Column,
  Spread,
  Actions,
} from 'braid-design-system';
import type { Maybe } from 'graphql/jsutils/Maybe';

import TrackingButtonLink from 'src/App/Components/TrackingButtonLink/TrackingButtonLink';
import { analyticsEvents } from 'src/constants/analytics';
import { TRACKING_DASHBOARD_TS_WIDGET } from 'src/constants/tracking';
import {
  useDashboardTalentSearch,
  useHirerAccount,
  useTalentSearchContext,
} from 'src/modules/ApiClient';
import { urlResolver } from 'src/utils/config';

import translations from './.vocab';
import NoTalentSearchImageSvg from './SVGs/NoTalentSearchImageSvg';
import PremiumTalentSearchImageSvg from './SVGs/PremiumTalentSearchImageSvg';
import TalentSearchImageSvg from './SVGs/TalentSearchImageSvg';

interface talentSearchTile {
  heading: string;
  text: string;
  buttonText: string;
  buttonURL: string;
  analyticsEvent: string;
  analyticsText: string;
  svg?: JSX.Element;
}

const getContent = (
  isTalentSearchApproved: Maybe<boolean>,
  url: string,
  hasPTS: boolean,
  translate: ReturnType<typeof useTranslations>['t'],
): talentSearchTile => {
  if (!isTalentSearchApproved) {
    return {
      heading: translate('No Talent Search access heading'),
      text: translate('No Talent Search access message'),
      buttonText: translate('Post a job ad button'),
      buttonURL: URL_CREATE_NEW_JOB,
      analyticsEvent: analyticsEvents.TRACK_CONTENT_ELEMENT,
      analyticsText: 'Post a job ad',
      svg: <NoTalentSearchImageSvg />,
    };
  }

  if (hasPTS) {
    return {
      heading: translate('Premium Talent Search enable heading'),
      text: translate('Premium Talent Search enable message'),
      buttonText: translate('Visit Talent Search Button'),
      buttonURL: url,
      analyticsEvent: analyticsEvents.TRACK_CONTENT_ELEMENT,
      analyticsText: 'Visit Talent Search',
      svg: <PremiumTalentSearchImageSvg />,
    };
  }

  return {
    heading: translate('Standard Talent Search promote heading'),
    text: translate('Standard Talent Search promote message'),
    buttonText: translate('Visit Candidate Matches Button'),
    buttonURL: url,
    analyticsEvent: analyticsEvents.TRACK_CONTENT_ELEMENT,
    analyticsText: 'Visit Candidate Matches',
    svg: <NoTalentSearchImageSvg />,
  };
};

const TalentSearch = () => {
  const { data: tsContext } = useTalentSearchContext();
  const { data: hirerDashboardTalentSearch } = useDashboardTalentSearch();
  const { data: hirerAccount } = useHirerAccount();
  const { t } = useTranslations(translations);

  if (!tsContext || !hirerDashboardTalentSearch || !hirerAccount) {
    return null;
  }

  const content = getContent(
    hirerAccount.isTalentSearchApproved,
    hirerDashboardTalentSearch.premiumTalentSearchHref!,
    tsContext.hasPTSClaim,
    t,
  );

  return (
    <Box
      display="flex"
      padding="large"
      background="formAccentSoft"
      borderRadius="large"
      height="full"
    >
      <Columns space="medium" collapseBelow="desktop" reverse>
        <Column width="content">
          <Box
            display="inlineBlock"
            background="surface"
            borderRadius="full"
            padding="medium"
          >
            {content.svg ? content.svg : <TalentSearchImageSvg />}
          </Box>
        </Column>
        <Column>
          <Spread space="medium" direction="vertical">
            <Stack space="medium">
              <Text component="header" size="large" weight="strong">
                {content.heading}
              </Text>
              <Text>{content.text}</Text>
            </Stack>

            <Actions>
              <TrackingButtonLink
                href={urlResolver({
                  path: content.buttonURL,
                })}
                target="_blank"
                rel="noopener"
                trackingEventName={content.analyticsEvent}
                trackingActionOrigin={TRACKING_DASHBOARD_TS_WIDGET}
                trackingData={{
                  isPrimaryCTA: true,
                  elementAction: 'internal redirect',
                  elementDesign: 'button',
                  elementText: content.analyticsText,
                }}
              >
                {content.buttonText}
              </TrackingButtonLink>
            </Actions>
          </Spread>
        </Column>
      </Columns>
    </Box>
  );
};

export default TalentSearch;
